import { enhanceLocationFilters } from './enhance-location-filters';

/** Reads `location` refinements from UI filter state (before SSR/client appends remote OR buckets). */
export function readPrimaryLocationFilterValues(
  filters: Record<string, unknown> | null | undefined,
): string[] {
  if (!filters) return [];
  const raw = filters.location;
  return Array.isArray(raw) ? raw.map(String).filter(Boolean) : [];
}

function collectHitLocationStrings(hit: Record<string, unknown>): string[] {
  const loc = hit.location;
  if (Array.isArray(loc)) {
    return loc.filter((x): x is string => typeof x === 'string' && x.trim() !== '');
  }
  if (typeof loc === 'string' && loc.trim() !== '') {
    return [loc];
  }
  return [];
}

export function hitMatchesAnyPrimaryLocation(
  hit: Record<string, unknown>,
  primaries: readonly string[],
): boolean {
  if (primaries.length === 0) return false;
  const primarySet = new Set(primaries);
  return collectHitLocationStrings(hit).some((v) => primarySet.has(v));
}

/**
 * When {@link enhanceLocationFilters} ORs in remote Europe/Global buckets, move hits that still
 * list a user-selected primary `location` ahead of hits that only matched via those appended
 * buckets — for the current page only (Algolia pagination order is unchanged beyond this slice).
 */
export function reorderSearchHitsForPrimaryLocationBoost<T extends Record<string, unknown>>(
  hits: T[],
  primaryLocations: string[],
  locale: string,
): T[] {
  const primary = [...new Set(primaryLocations.map((s) => String(s).trim()).filter(Boolean))];
  if (primary.length === 0 || hits.length === 0) {
    return hits;
  }

  const enhanced = enhanceLocationFilters([...primary], locale);
  const primarySet = new Set(primary);
  const appendedRemoteBuckets = enhanced.some((loc) => !primarySet.has(loc));
  if (!appendedRemoteBuckets) {
    return hits;
  }

  const matching: T[] = [];
  const nonMatching: T[] = [];
  for (const h of hits) {
    if (hitMatchesAnyPrimaryLocation(h as Record<string, unknown>, primary)) {
      matching.push(h);
    } else {
      nonMatching.push(h);
    }
  }
  return [...matching, ...nonMatching];
}
